import { HttpHeaders, HttpResponse } from '../../interfaces/api/http';
import http from '../http';
import api from '../../config/api';
import { AuthLoginResponse, LoginResponse } from '../../interfaces/api/auth';
import { LMSUserDetails, LoginBody, AuthLoginUserDetails } from '../../interfaces/core/lmsCore';
import axios, { AxiosResponse } from 'axios';

/**
 * Authentication service module.
 * Provides methods for user login and logout, interacting with the backend APIs.
 */
const authentication = {
  authLogin: async (body: LoginBody): Promise<AuthLoginResponse> => {
    try {
      const baseURL = process.env.REACT_APP_AUTH_URL;
      if (!baseURL) {
        throw new Error('REACT_APP_AUTH_URL environment variable is not defined');
      }

      const response: AxiosResponse<AuthLoginResponse> = await axios.post(`${baseURL}login`, body);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(`Auth login failed: ${error.response?.data?.message || error.message}`);
      }
      throw error;
    }
  },

  /**
   * Performs a two-step login process:
   * 1. First calls authLogin to get user details
   * 2. Then calls login to get the session token
   *
   * @param {LoginBody} authBody - The authentication credentials (username, password, etc.)
   * @param {string} password - The password for the second login step
   * @param {HttpHeaders} headers - Additional HTTP headers for the request
   * @returns {Promise<HttpResponse<LoginResponse>>} - A promise resolving to the login response
   */
  twoStepLogin: async (authBody: LoginBody, password: string, headers: HttpHeaders): Promise<HttpResponse<LoginResponse>> => {
    try {
      // Step 1: Get user details from authLogin
      const authResponse = await authentication.authLogin(authBody);
      
      // Step 2: Prepare user details for the second login
      const currentUser = authResponse.account;
      const options: LMSUserDetails = Object.entries({
        name: currentUser?.full_name || 'Temp User',
        email: currentUser?.email || '',
        password,
        first_name: currentUser?.first_name || '',
        last_name: currentUser?.last_name || '',
        bod: currentUser?.bod !== '0000-00-00' ? currentUser?.bod : '',
        info_email: currentUser?.info_email || '',
        mobile: currentUser?.mobile || '',
        hospital: currentUser?.hospital || '',
        city: currentUser?.city || '',
        pharmacy_name: currentUser?.pharmacy_name || '',
        gender: currentUser?.gender || '',
        image: currentUser?.image || '',
      }).reduce((acc, [key, value]) => {
        if (value !== '') {
          acc[key] = value;
        }
        return acc;
      }, {} as LMSUserDetails);

      if (!options.name || !options.email) {
        throw new Error('Invalid user details received from auth login');
      }

      // Step 3: Call the regular login API with the prepared user details
      return await authentication.login(options, headers);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(`Two-step login failed: ${error.response?.data?.message || error.message}`);
      }
      throw error;
    }
  },

  /**
   * Logs in a user by sending credentials to the login endpoint.
   *
   * @param {LMSUserDetails} body - The user's login details (e.g., name and email).
   * @param {HttpHeaders} headers - Additional HTTP headers for the request.
   * @returns {Promise<HttpResponse<LoginResponse>>} - A promise resolving to the login response.
   *
   * @example
   * const body = { name: 'John Doe', email: 'john.doe@example.com' };
   * const headers = { Authorization: 'Bearer token' };
   * authentication.login(body, headers).then((response) => {
   *   console.log(response);
   * }).catch((error) => {
   *   console.error(error);
   * });
   */
  login: (body: LMSUserDetails, headers: HttpHeaders): Promise<HttpResponse<LoginResponse>> => {
    return http.post<LoginResponse>(api.login, body, headers);
  },

  /**
   * Logs out the current user by hitting the logout endpoint.
   *
   * @returns {Promise<HttpResponse<null>>} - A promise resolving to the logout response.
   *
   * @example
   * authentication.logout().then(() => {
   *   console.log('User logged out');
   * }).catch((error) => {
   *   console.error(error);
   * });
   */
  logout: (): Promise<HttpResponse<null>> => {
    return http.get<null>(api.logout);
  },
};

export default authentication;
